/**
 * Die Zusammensetzung des Verbrauchs (HM-118, `VerbraucherAufteilung` aus
 * `ui_kits/dashboard/loads.jsx`): erst ein Balken über die volle Breite, dann
 * je Teil eine Zeile. Im Haus sind das die Verbraucher mit ihrem Zähler und
 * zuletzt der übrige Verbrauch; im Mieterstrom zuerst die Wohneinheiten
 * zusammen, dann die weiteren Verbraucher (`lib/consumption-split.ts`).
 *
 * **Übernommen ist der Aufbau, nicht jede Angabe.** Der Entwurf nennt unter dem
 * Namen eine Leistungsangabe („11 kW · go-e Charger") — ein Feld dafür hat der
 * Verbraucher nicht, die Zeile bleibt dort leer. Die aufklappbare
 * Liste „Verbraucher im Rest enthalten" fehlt ebenfalls: Einen Verbraucher ohne
 * eigenen Zähler kennt das Datenmodell nicht — ein Steckplatz ohne Zähler ist
 * eine offene Pflicht, kein Verbraucher im Rest.
 *
 * **Seit HM-146 folgt sie dem überarbeiteten Entwurf**: die Summe über dem
 * Balken, „davon gemessen" daneben, und — solange der Zeitraum das Jetzt
 * enthält — die **Leistung jetzt**: ein schmaler zweiter Balken nach derselben
 * Aufteilung, „Aktuelle Leistung" darunter und je Zeile eine Spalte mit der
 * Leistung des Teils (`lib/composition-live.ts`). **Die Herkunft aus PV und
 * Netz je Teil (HM-120) ist dabei entfallen** (Auftraggeber): Der Entwurf
 * zeigt sie nicht, und die Karte beantwortet eine Frage — woraus der Verbrauch
 * besteht. Die API liefert `origin` weiter.
 *
 * **Seit dem Nachtrag zu HM-150 ist die Karte ruhiger** (Auftraggeber): Unter
 * dem Titel steht nur der Zeitraum, der erklärende Absatz am Fuß ist entfallen,
 * und dort steht stattdessen die Legende für PV-Anlage und Netz
 * (`OriginLegend`) — einmal für die ganze Karte.
 */
import { Badge, Card, icons, quantityColorVar, useNarrow } from "@hausmeter/ui";
import {
  ENERGY_UNIT,
  formatEnergy,
  formatPercent,
  formatPowerFull,
  totalNote,
} from "../lib/energy-format.js";
import type { CompositionLive } from "../lib/composition-live.js";
import type { ConsumptionSplit, SplitPart } from "../lib/consumption-split.js";

export interface PartMeter {
  readonly label: string;
  readonly number: string;
}

export const MONO = { fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" } as const;

/** Die Leistung jetzt — in W oder kW, je nach Betrag; die Einheit steht deshalb am Wert. */
const kw = (watts: number): string => formatPowerFull(watts);

/** Energie in kWh, die Stellen nach dem Betrag — die Verteilung unter einem Wert (HM-150). */
const kwh = (wh: number): string => formatEnergy(wh);

const PV_COLOR = quantityColorVar("self_consumption");
const GRID_COLOR = quantityColorVar("grid_import");

/** PV-Anteil und Netzanteil als Paar, in der Einheit des Werts darüber. */
export interface OriginPair {
  readonly pv: number;
  readonly grid: number;
}

/**
 * **Die Verteilung** (HM-150, Entwurf): klein unter einem Wert, ein Blatt mit
 * dem Anteil aus der PV-Anlage, ein Blitz mit dem aus dem Netz. Gerechnet hat
 * der Server — je Viertelstunde bzw. jetzt nach der Regel des Messkonzepts —,
 * hier wird nur geschrieben.
 */
export function Origin({ origin, unit }: { origin: OriginPair; unit: "kWh" | "kW" }) {
  // Energie steht immer in kWh und trägt die Einheit im Untertitel; eine
  // Leistung wechselt zwischen W und kW und trägt sie deshalb selbst.
  const text = (value: number) => (unit === "kWh" ? kwh(value) : kw(value));
  const titled = (value: number) => (unit === "kWh" ? `${kwh(value)} ${ENERGY_UNIT}` : kw(value));
  return (
    <span
      title={`aus der PV-Anlage ${titled(origin.pv)} · aus dem Netz ${titled(origin.grid)} — nach der Regel des Messkonzepts`}
      style={{
        ...MONO,
        display: "inline-flex",
        justifyContent: "flex-end",
        gap: 6,
        fontSize: 11,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 2, color: PV_COLOR }}>
        <icons.IconLeaf size={11} strokeWidth={2.4} aria-hidden />
        {text(origin.pv)}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 2, color: GRID_COLOR }}>
        <icons.IconBolt size={11} strokeWidth={2.4} aria-hidden />
        {text(origin.grid)}
      </span>
    </span>
  );
}

/** Die Verteilung aus einer Herkunft der API — `null`, wo ein Anteil fehlt. */
export function originPairOf(
  origin: { readonly pv: number | null; readonly grid: number | null } | undefined,
): OriginPair | null {
  return origin?.pv == null || origin.grid == null ? null : { pv: origin.pv, grid: origin.grid };
}

/** Die Verteilung jetzt eines Teils — `null` ohne PV-Anteil. */
export function liveOriginOf(live: CompositionLive, key: string | null): OriginPair | null {
  if (live.pvByKey === null || live.pvW === null) return null;
  const watts = key === null ? live.totalW : (live.byKey.get(key) ?? 0);
  const pv = key === null ? live.pvW : (live.pvByKey.get(key) ?? 0);
  return { pv, grid: Math.max(watts - pv, 0) };
}

/** Die Summe über dem Balken (HM-146): links das Ganze, rechts was davon gemessen ist. */
export function TotalHeader({
  label,
  valueWh,
  note,
  measuredShare,
  origin = null,
}: {
  label: string;
  valueWh: number;
  note: string | null;
  measuredShare: number | null;
  origin?: OriginPair | null;
}) {
  return (
    <div
      style={{
        marginBottom: 6,
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
        fontSize: 11.5,
        color: "var(--text-faint)",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span>
          {label}{" "}
          <span style={{ ...MONO, fontWeight: 600, color: "var(--text-muted)" }}>
            {formatEnergy(valueWh)} {ENERGY_UNIT}
          </span>
          {note === null ? "" : ` · ${note}`}
        </span>
        {origin !== null && <Origin origin={origin} unit="kWh" />}
      </span>
      {measuredShare !== null && (
        <span style={{ whiteSpace: "nowrap" }}>davon gemessen {formatPercent(measuredShare)}</span>
      )}
    </div>
  );
}

/**
 * Der schmale zweite Balken: dieselben Teile, aufgeteilt nach der Leistung
 * jetzt, und die Gesamtleistung darunter (HM-146).
 */
export function LiveBar({
  live,
  segments,
}: {
  live: CompositionLive;
  segments: readonly { readonly key: string; readonly label: string; readonly color: string }[];
}) {
  return (
    <>
      {live.totalW > 0 && (
        <div
          style={{
            marginTop: 4,
            display: "flex",
            height: 10,
            borderRadius: "var(--radius-sm)",
            overflow: "hidden",
            background: "var(--surface-sunken)",
          }}
        >
          {segments.map((segment) => {
            const watts = live.byKey.get(segment.key) ?? 0;
            return watts <= 0 ? null : (
              <span
                key={segment.key}
                title={`${segment.label} · ${kw(watts)} · ${formatPercent(watts / live.totalW)}`}
                style={{ width: `${(watts / live.totalW) * 100}%`, background: segment.color }}
              />
            );
          })}
        </div>
      )}
      <div
        style={{
          marginTop: 6,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          fontSize: 11.5,
          color: "var(--text-faint)",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span>
            Aktuelle Leistung{" "}
            <span style={{ ...MONO, fontWeight: 600, color: "var(--text-muted)" }}>
              {kw(live.totalW)}
            </span>
          </span>
          {liveOriginOf(live, null) !== null && <Origin origin={liveOriginOf(live, null)!} unit="kW" />}
        </span>
      </div>
    </>
  );
}

/**
 * **Die Legende steht am Fuß der Karte** — einmal für Balken und Zeilen, nicht
 * mehr in der Zeile der Leistung: Dort fehlte sie, sobald der Zeitraum das
 * Jetzt nicht enthielt, obwohl Blatt und Blitz an jeder Zeile standen.
 */
export function OriginLegend() {
  return (
    <div
      style={{
        marginTop: 12,
        display: "flex",
        justifyContent: "flex-end",
        gap: 12,
        fontSize: 11.5,
        color: "var(--text-faint)",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <span style={{ color: PV_COLOR, display: "inline-flex" }}>
          <icons.IconLeaf size={12} strokeWidth={2.4} aria-hidden />
        </span>
        PV-Anlage
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <span style={{ color: GRID_COLOR, display: "inline-flex" }}>
          <icons.IconBolt size={12} strokeWidth={2.4} aria-hidden />
        </span>
        Netz
      </span>
    </div>
  );
}

/**
 * Der Untertitel einer Zusammensetzung: **nur der Zeitraum** (Auftraggeber) —
 * „Über den Tag". Was die Karte aufteilt, sagen Titel und Zeilen; die Einheit
 * steht an jedem Wert.
 */
export function periodSubtitle(caption: string): string {
  return caption.charAt(0).toUpperCase() + caption.slice(1);
}

/** Die Leistung jetzt in einer Zeile — mit Punkt, grün, solange etwas fließt. */
export function LiveCell({
  watts,
  rest,
  origin = null,
}: {
  watts: number;
  rest: boolean;
  origin?: OriginPair | null;
}) {
  return (
    <span
      role="cell"
      title={rest ? "Aktuelle Leistung · gerechnet" : "Aktuelle Leistung"}
      style={{
        ...MONO,
        textAlign: "right",
        whiteSpace: "nowrap",
        paddingRight: 14,
        borderRight: "1px solid var(--border-subtle)",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 5,
          fontSize: 12,
          fontWeight: 500,
          color: watts > 0 ? "var(--text-muted)" : "var(--text-faint)",
        }}
      >
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: 99,
            background: watts > 0 ? "var(--hm-success)" : "var(--border-default)",
          }}
        />
        {kw(watts)}
      </span>
      {origin !== null && watts > 0 && (
        <span style={{ display: "flex", justifyContent: "flex-end" }}>
          <Origin origin={origin} unit="kW" />
        </span>
      )}
    </span>
  );
}

/**
 * Unter dem Wert einer Zeile: die Verteilung, wo es sie gibt, sonst der
 * Anteil am Ganzen — wie im Entwurf steht nur eines von beiden da.
 */
export function ValueCell({
  valueWh,
  share,
  origin,
}: {
  valueWh: number | null | undefined;
  share: number | null;
  origin: OriginPair | null;
}) {
  return (
    <span role="cell" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
      <span
        title={share === null ? undefined : `${formatPercent(share)} des Ganzen`}
        style={{
          ...MONO,
          display: "block",
          fontSize: 15,
          fontWeight: 600,
          color: "var(--text-strong)",
        }}
      >
        {valueWh == null ? "–" : `${formatEnergy(valueWh)} ${ENERGY_UNIT}`}
      </span>
      {origin !== null ? (
        <span style={{ display: "flex", justifyContent: "flex-end" }}>
          <Origin origin={origin} unit="kWh" />
        </span>
      ) : (
        share !== null && (
          <span style={{ ...MONO, display: "block", fontSize: 11.5, color: "var(--text-muted)" }}>
            {formatPercent(share)}
          </span>
        )
      )}
    </span>
  );
}

/** Die Spalten einer Zeile — mit Leistung jetzt eine schmale Spalte mehr. */
export function compositionColumns(narrow: boolean, live: boolean): string {
  return `${narrow ? "minmax(0, 1fr)" : "minmax(0, 1.9fr) minmax(0, 1.1fr)"}${live ? " 92px" : ""} 7.5rem`;
}

export function ConsumptionComposition({
  split,
  caption,
  meterOf,
  live = null,
}: {
  split: ConsumptionSplit;
  caption: string;
  /**
   * Der Zähler eines Verbrauchers, über die Messstelle seines Steckplatzes.
   * Ohne ihn gibt es keine Zählerspalte — an der Wohnung kennt ein Bewohner die
   * Zuordnung nicht, und geraten wird sie nicht.
   */
  meterOf?: (part: SplitPart) => PartMeter | null;
  /** Die Leistung jetzt je Teil — nur, solange der Zeitraum das Jetzt enthält (HM-146). */
  live?: CompositionLive | null;
}) {
  const narrow = useNarrow(720) || meterOf === undefined;
  const parts = split.parts;
  const whole = split.consumption.value;
  const note = totalNote(split.consumption);
  /* Die Legende nur, wo Blatt und Blitz irgendwo in der Karte stehen. */
  const hasOrigin =
    originPairOf(split.consumption.origin) !== null ||
    parts.some((part) => originPairOf(part.origin) !== null) ||
    (live !== null && liveOriginOf(live, null) !== null);

  const row = (part: SplitPart) => {
    const meter = part.kind === "consumer" && meterOf !== undefined ? meterOf(part) : null;
    const sub =
      part.kind === "rest"
        ? "Verbrauch − Verbraucher · nicht einzeln gemessen"
        : part.kind === "units"
          ? `${part.count === 1 ? "1 Wohneinheit" : `${part.count} Wohneinheiten`} · einzeln unter „Wohneinheiten"`
          : null;
    return (
      <div
        key={part.key}
        role="row"
        style={{
          display: "grid",
          /* Jede Zeile ist ein eigenes Raster — mit `auto` rechts stünde die
             mittlere Spalte je nach Länge der Zahl woanders. */
          gridTemplateColumns: compositionColumns(narrow, live !== null),
          gap: 14,
          alignItems: "center",
          padding: "12px 0",
          borderTop: "1px solid var(--border-subtle)",
        }}
      >
        <span role="cell" style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          {part.shade !== null && (
            <span
              aria-hidden
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                flexShrink: 0,
                background: quantityColorVar(part.shade),
              }}
            />
          )}
          <span style={{ minWidth: 0 }}>
            <span
              style={{
                display: "block",
                fontSize: 13.5,
                fontWeight: 600,
                color: "var(--text-strong)",
              }}
            >
              {part.label}
            </span>
            {sub !== null && (
              <span style={{ display: "block", fontSize: 11.5, color: "var(--text-faint)" }}>
                {sub}
              </span>
            )}
            {narrow && meter !== null && (
              <span style={{ display: "block", fontSize: 11.5, color: "var(--text-muted)" }}>
                {meter.label} · <span style={MONO}>{meter.number}</span>
              </span>
            )}
          </span>
        </span>
        {!narrow && (
          <span role="cell" style={{ minWidth: 0 }}>
            {part.kind === "rest" ? (
              <span style={{ fontSize: 12, color: "var(--text-faint)" }}>kein eigener Zähler</span>
            ) : part.kind === "units" ? (
              <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
                je Wohnung ein Zähler
              </span>
            ) : meter === null ? null : (
              <>
                <span style={{ display: "block", fontSize: 12.5, color: "var(--text-body)" }}>
                  {meter.label}
                </span>
                <span
                  style={{ ...MONO, display: "block", fontSize: 11.5, color: "var(--text-muted)" }}
                >
                  {meter.number}
                </span>
              </>
            )}
          </span>
        )}
        {live !== null && (
          <LiveCell
            watts={live.byKey.get(part.key) ?? 0}
            rest={part.kind === "rest"}
            origin={liveOriginOf(live, part.key)}
          />
        )}
        <ValueCell
          valueWh={part.total?.value}
          share={part.share}
          origin={originPairOf(part.origin)}
        />
      </div>
    );
  };

  return (
    <Card
      title="Zusammensetzung des Verbrauchs"
      subtitle={periodSubtitle(caption)}
      action={
        <Badge tone="neutral">
          {split.consumerCount === 1 ? "1 Verbraucher" : `${split.consumerCount} Verbraucher`}
        </Badge>
      }
    >
      {/* Ein Balken über die volle Breite: Die Anteile sind Teile eines Ganzen,
          und das Ganze ist der Verbrauch. Ohne Stapelfarben gibt es ihn nicht —
          gleich gefärbte Streifen wären nicht auseinanderzuhalten. */}
      {split.stackable && whole !== null && whole > 0 && (
        <>
          <TotalHeader
            label="Gesamtverbrauch"
            valueWh={whole}
            note={note}
            measuredShare={split.measuredShare}
            origin={originPairOf(split.consumption.origin)}
          />
          <div
            role="img"
            aria-label={parts
              .filter((part) => part.share !== null)
              .map((part) => `${part.label} ${formatPercent(part.share!)}`)
              .join(", ")}
            style={{
              display: "flex",
              height: 34,
              borderRadius: "var(--radius-sm)",
              overflow: "hidden",
              background: "var(--surface-sunken)",
            }}
          >
            {parts.map((part) =>
              part.share === null || part.share <= 0 ? null : (
                <span
                  key={part.key}
                  title={`${part.label} · ${formatEnergy(part.total!.value!)} ${ENERGY_UNIT} · ${formatPercent(part.share)}`}
                  style={{
                    width: `${Math.min(100, part.share * 100)}%`,
                    background: quantityColorVar(part.shade!),
                  }}
                />
              ),
            )}
          </div>
          {live !== null && (
            <LiveBar
              live={live}
              segments={parts.map((part) => ({
                key: part.key,
                label: part.label,
                color: quantityColorVar(part.shade!),
              }))}
            />
          )}
        </>
      )}

      <div role="table" aria-label="Zusammensetzung des Verbrauchs" style={{ marginTop: 14 }}>
        {parts.map(row)}
      </div>

      {hasOrigin && <OriginLegend />}
    </Card>
  );
}
